import { useState, type ReactNode } from 'react';

/**
 * Site-wide password gate. Wraps the entire app.
 *
 * Security posture: this is a deterrent. The JSON payload at /data/*.json
 * is still fetchable by anyone who knows the URL structure. If you need
 * real auth, put Cloudflare Access in front of the whole project.
 *
 * The password is never stored in plaintext in the bundle; we compare
 * a SHA-256 digest against a constant hash baked into the source.
 */

const UNLOCK_FLAG = 'posmatraci-site-unlocked';

// SHA-256 of the configured password (lowercase hex).
// To change: echo -n 'NEW-PASSWORD' | shasum -a 256
// Then update EXPECTED_HASH below and redeploy.
const EXPECTED_HASH =
  'd789311ca7f775b883e63fa9680cff656b48c5b89f65413309e386cc175a3042';

async function sha256(str: string): Promise<string> {
  const buffer = new TextEncoder().encode(str);
  const hash = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

interface Props {
  children: ReactNode;
}

export default function SiteGate({ children }: Props) {
  const [unlocked, setUnlocked] = useState<boolean>(
    () =>
      typeof localStorage !== 'undefined' &&
      localStorage.getItem(UNLOCK_FLAG) === '1',
  );
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const digest = await sha256(input.trim());
    if (digest === EXPECTED_HASH) {
      localStorage.setItem(UNLOCK_FLAG, '1');
      setUnlocked(true);
      setError(null);
    } else {
      setError('Pogrešna lozinka.');
    }
  };

  if (unlocked) {
    return <>{children}</>;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-paper px-6">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <p className="font-mono text-xs uppercase tracking-widest text-neutral-500">
            posmatrači
          </p>
          <h1 className="mt-3 font-serif text-3xl text-ink">Šta su posmatrači rekli</h1>
          <p className="mt-3 text-sm text-neutral-600">
            Interna analiza — pristup samo sa lozinkom.
          </p>
        </div>
        <form
          onSubmit={handleSubmit}
          className="rounded-lg border border-neutral-200 bg-white p-6 shadow-sm"
        >
          <label htmlFor="site-pw" className="block text-sm font-medium text-neutral-700">
            Lozinka
          </label>
          <input
            id="site-pw"
            type="password"
            autoFocus
            autoComplete="off"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            className="mt-2 w-full rounded-md border border-neutral-300 bg-paper px-3 py-2 text-ink focus:border-axis-op focus:outline-none focus:ring-2 focus:ring-axis-op/30"
          />
          {error && (
            <p className="mt-2 text-sm text-axis-sn-ink">{error}</p>
          )}
          <button
            type="submit"
            className="mt-4 w-full rounded-md bg-axis-op px-4 py-2 text-white transition-colors hover:bg-axis-op-ink"
          >
            Otvori sajt
          </button>
        </form>
      </div>
    </div>
  );
}
